import { DefaultMessages, ProgramThemeMessages, SegmentSpecificMessages } from '@sheerid/jslib';
import { RecursivePartial } from '../types/types';


type LibraryMessages = DefaultMessages & SegmentSpecificMessages & ProgramThemeMessages;

/**
 * Feel free to modify this type for this project (only) here
 * For example, if you add a whole new block of text that the jslib does not have a spot for already
 *
 * This is not necessary if you are just overriding text for a pre-existing message id
 */
type ThisProjectMessages = {
    softwareTypes: {
        online: string,
        desktop: string,
        tax: string,
    },
    errorId: {
        invalidCourseType: string,
        invalidQuickBooksDesktopVersion: string,
        invalidSoftwareType: string,
        invalidConfirmEmail: string,
        nonMatchEmail: string,
    },
    step: {
        personalInfo: {
            schoolNotFound: string,
            whySchoolEmail: string,
        },
        success: {
            createAccount: string,
            onlineCreateAccount: string,
            online: {
                title: string,
                subtitle: string,
                accessLink: string,
                bestWishes: string,
                bullet1: string,
                bullet2: string,
                nextSteps: string,
                onlineLogin: string,
                subtitleCA: string,
                nextStepsCA: string,
            },
            desktop: {
                title: string,
                subtitle: string,
                subtitleCA: string,
                accessLink: string,
                bestWishes: string,
                contactIntuit: string,
            },
            tax: {
                title: string,
                subtitleCA: string,
                accessLink: string,
                bulletOne: string,
                bulletTwo: string,
                bulletThree: string,
                bestWishes: string,
                downloadLinkInstructionsCA: string,
            },
        },
        docUpload: {
            footer: string,
        },
    },
    birthDatePlaceholder: string,
    confirmEmailAddress: string,
    other: string,
    'accounting/bookkeeping': string,
    entrepreneurship: string,
    business: string,
    tax: string,
    quickbooks: string,
    courseType: string,
    quickbooksDesktopVersion: string,
    returnToIntuit: string,
    intuitEducationTeam: string,
    contactIntuit: string,
    infoShared: string,
    schoolNotListed: string,
    emailToolTip: string,
};

/**
 * Define the actual language strings here, as needed.
 * This object should be sparse, so you inherit language from the library. Only override what you need to.
 *
 * The complicated type ensures your object adheres to the library's type(s), as well as any additional type you've
 * created in ThisProjectMessages.
 */
export const messagesFR: RecursivePartial<LibraryMessages & ThisProjectMessages> = {
    firstName: 'Prénom',
    lastName: 'Nom de famille',
    birthDate: 'Date de naissance',
    birthDatePlaceholder: 'aaaa/mm/jj',
    emailAddress: "Adresse courriel fournie par l'établissement",
    confirmEmailAddress: "Confirmez l'adresse courriel",
    schoolName: "Nom de l'établissement",
    verifyMyTeacherStatus: 'Vérifier et continuer',
    other: 'Autre',
    'accounting/bookkeeping': 'Comptabilité/Tenue de comptes',
    entrepreneurship: 'Entrepreneuriat',
    business: 'Entreprises',
    tax: 'Taxe',
    schoolNotListed: "L'établissement éducatif n'est pas dans la liste?",
    emailToolTip: 'Pourquoi?',
    quickbooks: 'QuickBooks',
    courseType: 'Type de cours',
    quickbooksDesktopVersion: 'Version de QuickBooks Desktop',
    returnToIntuit: 'Retourner à Intuit',
    intuitEducationTeam: "– L'équipe Intuit",
    contactIntuit: 'Si ce lien ne fonctionne pas, <a class="sid-link" href="mailto:iepcanada@intuit.com">contactez le soutien</a>.',
    softwareTypes: {
        online: 'QuickBooks en ligne',
        desktop: 'QuickBooks Desktop',
        tax: 'ProFile',
    },
    error: "Une erreur s'est produite",
    errorId: {
        invalidCourseType: 'Type de cours non valide',
        invalidQuickBooksDesktopVersion: 'Version non valide de QuickBooks Desktop',
        invalidSoftwareType: 'Veuillez retourner à Intuit.',
        invalidConfirmEmail: 'Adresse courriel de confirmation non valide',
        nonMatchEmail: 'Les adresses courriel ne sont pas identiques',
    },
    step: {
        personalInfo: {
            title: 'Inscription des enseignants',
            subtitle: 'Les enseignants et le personnel des établissements universitaires situés au Canada sont admissibles. Remplissez le formulaire ci-dessous pour confirmer votre admissibilité et déverrouiller votre licence gratuite pour {software}.',
            schoolNotFound: 'Pour savoir si votre établissement est admissible, écrivez-nous à <a class="sid-link" href="mailto:education@intuit.com">education@intuit.com</a>.',
            whySchoolEmail: "L'adresse courriel fournie par votre établissement nous permet de vérifier rapidement si vous êtes admissible à cette offre réservée aux enseignants.",
            howDoesVerifyingWorkDetails: "Pour prétendre à cette offre, vous devez être employé comme enseignant, professeur ou membre du personnel dans une\n            université K-12\n au Canada.\n                <br><br>\n            {companyName} utilise SheerID, un partenaire de confiance, pour vérifier que vous êtes un enseignant, professeur ou membre\n                du personnel. SheerID ne recueille que des informations qui aident à vérifier votre éligibilité. Des informations\n            ou documents supplémentaires peuvent être demandés.",
        },
        docUpload: {
            title: 'Documents de vérification',
            subtitle: "Nous n'avons pas pu vérifier votre statut d'enseignant.<br />Les documents seront vérifiés d'ici {estimatedWaitTime} par notre partenaire SheerID.",
            submitButtonLabel: 'Envoyer des documents',
            uploadInstructions: 'Téléversez vos documents scolaires indiquant:',
            footer: "L'image des documents doit être claire et lisible et montrer le nom légal sous lequel vous êtes inscrit auprès de l'établissement d'enseignement. <strong>Ne transmettez pas d'information confidentielle comme votre numéro de sécurité sociale ou vos renseignements bancaires.</strong> Masquez ces renseignements à la main avant de numériser les documents ou utilisez un logiciel de traitement d'images.",
            howDoesVerifyingWorkDetails: `Les documents seront vérifiés par SheerID, un partenaire de confiance spécialisé dans la vérification de renseignements sur les enseignants.
            <br><br>
            Les documents serviront uniquement à vérifier votre statut ou celui de votre faculté et seront supprimés
             après la vérification. Ils ne seront pas communiqués à {companyName}.`,
        },
        pending: {
            titleReview: 'Document en cours de vérification',
            turnaroundTime: "Nous avons reçu votre document. Nous vérifions votre statut d'inscription.",
            subtitle2: 'Cela devrait prendre moins de 20 minutes. Cette page sera actualisée lorsque votre statut aura été vérifié, et vous recevrez un courriel indiquant votre statut. Vous pouvez donc fermer cette page.',
            subtitle3: 'Si vous croyez ne pas avoir reçu de courriel, vérifiez si votre dossier de pourriel contient un message de verify@sheerid.com.',
        },
        success: {
            title: "Bienvenue au programme d'éducation d'Intuit!",
            createAccount: '<a class="sid-link" href="{accessLink}">Créez votre compte</a>',
            onlineCreateAccount: "<a class='sid-link' href='{accessLink}'>Créez le compte QuickBooks en ligne de votre entreprise</a>",
            online: {
                title: "Bienvenue à QuickBooks en-ligne",
                subtitle: 'Votre inscription a été confirmée. Utilisez le portail Intuit des enseignants pour :',
                subtitleCA: "Votre inscription a été confirmée.",
                bestWishes: 'Nous vous souhaitons bonne chance dans votre enseignement de QuickBooks!',
                bullet1: 'Préparer des cours',
                bullet2: 'Inviter les étudiants à créer leur propre entreprise QuickBooks en ligne Plus.',
                nextSteps: 'Pour accéder au portail Intuit des enseignants :',
                nextStepsCA: 'Étapes suivantes:',
                onlineLogin: '<a class="sid-link" href="{educatorPortalLink}">Ouvrez une session sur le portail Intuit des enseignants</a> avec vos identifiants QuickBooks en ligne Comptable.',
            },
            desktop: {
                title: "Bienvenue au programme d'éducation d'Intuit!",
                subtitle: "Votre statut d'étudiant a été confirmé. Nous vous ferons parvenir votre logiciel QuickBooks Desktop dès que possible.",
                subtitleCA: "Votre statut d'étudiant a été confirmé. Nous vous ferons parvenir votre logiciel QuickBooks Desktop dès que possible.",
                accessLink: "Vous recevrez un courriel indiquant vos renseignements d'enregistrement du logiciel dans un délai de trois jours ouvrables.",
                bestWishes: 'Nous vous souhaitons bonne chance dans votre enseignement de QuickBooks!',
                contactIntuit: "Si vous ne recevez pas le courriel, n'essayez pas de vous réinscrire et écrivez-nous à <a class='sid-link' href='mailto:education@intuit.com'>education@intuit.com</a>.",
            },
            tax: {
                title: "Bienvenue à Profile d'Intuit.",
                subtitleCA: "Nous avons le plaisir de vous offrir un accès gratuit au logiciel fiscal ProFile.",
                accessLink: '<a class="sid-link" href="{accessLink}">Créez votre compte</a>',
                downloadLinkInstructionsCA: 'Vous recevrez vos instructions de téléchargement et votre clé de licence dans les 3 jours ouvrables.',
                bestWishes: 'Nous vous souhaitons bonne chance dans votre parcours d’enseignant avec ProFile ! Si vous avez des questions, le service d’assistance pédagogique <a href="mailto:iepcanada@intuit.com">iepcanada@intuit.com</a>, peut vous aider.',
            },
        },
    },
    infoShared: "L'information saisie dans ce formulaire sera utilisée aux fins de vérification. Cette information sera communiquée à Intuit. L'utilisation des produits Intuit doit respecter les réglementations des États-Unis relatives au contrôle des exportations et aux sanctions économiques. <br /><br />Intuit utilisera vos renseignements personnels afin de configurer et de gérer votre essai gratuit et de vous communiquer des renseignements importants sur votre service. Pour consulter notre Déclaration de confidentialité, <a class='sid-link' href='https://security.intuit.com/index.php/privacy' target='_blank'>cliquez ici</a>.",
};

